import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useProjects } from "@/hooks/useProjects";
import { useTaskMutations } from "@/hooks/useTasks";
import { useTaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import { useTaskSubscription } from "@/hooks/useTaskSubscription";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useUsers } from "@/hooks/useUsers";
import { usePlots } from "@/hooks/usePlots";
import { useProcurementPackages } from "@/hooks/useProcurementPackages";
import { useSections } from "@/hooks/useSections";
import { useAllLocations } from "@/hooks/useAllLocations";
import { useMyProjectIds } from "@/hooks/useMyProjectIds";
import { useTeamsAuth } from "@/components/providers/TeamsAuthProvider";
import { useAuth } from "@/contexts/AuthContext";
import { TaskStatus, type TaskExtended } from "@/types";
import { useCascadingFilterScopes } from "@/components/tasks/hooks/useCascadingFilterScopes";
import { useServerFilterParams } from "@/components/tasks/hooks/useServerFilterParams";
import { useColumnRefetch } from "@/components/tasks/hooks/useColumnRefetch";
import { CreateTaskDialog } from "@/components/tasks/CreateTaskDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MultiSelectDropdown } from "@/components/ui/multi-select-dropdown";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { MobileTaskList } from "@/components/tasks/MobileTaskList";
import {
  SearchIcon,
  CloseIcon,
  AddIcon,
  FiltersIcon,
  InspectionTaskIcon,
} from "@/lib/icons";
import { MemoListToggle } from "@/components/memo/MemoListToggle";
import { Badge } from "@/components/ui/badge";
import { useTaskFilterStore } from "@/stores/useTaskFilterStore";
import { TASK_STATUS_LABELS_SINGULAR } from "@/lib/taskStatusMeta";
import { KANBAN_COLUMNS, TASK_TYPE_OPTIONS } from "@/lib/taskBoardMeta";
import { WeekSelector } from "@/components/tasks/WeekSelector";
import { MemoListStack } from "@/components/memo/MemoListStack";
import { useTaskWeekBuckets } from "@/hooks/useTaskWeekBuckets";
import { buildBucketFilterParams } from "@/components/tasks/hooks/useServerFilterParams";

type StatusTabValue = "PENDING" | "IN_PROGRESS" | "COMPLETED";

const STATUS_ORDER: StatusTabValue[] = ["PENDING", "IN_PROGRESS", "COMPLETED"];

export function TehtavahallintaMobile() {
  const { projects, loading: projectsLoading } = useProjects();
  const { users } = useUsers();
  const { plots } = usePlots();
  const { procurementPackages } = useProcurementPackages();
  const { sections } = useSections();
  const { locations } = useAllLocations();
  const { user } = useTeamsAuth();
  const { dbUser } = useAuth();

  // ADMIN / INTERNAL+ see every hanke server-side without holding a project
  // role, so the "omat hankkeet" narrowing below must not apply to them.
  const seesAllProjects =
    dbUser?.accessLevel === "ADMIN" || dbUser?.accessLevel === "INTERNAL_PLUS";

  const currentUserDbId = useMemo(() => {
    if (!user?.id) return undefined;
    return users.find((u) => u.azureObjectId === user.id)?.id;
  }, [users, user?.id]);

  const { myProjectIdSet, loading: myProjectRolesLoading } =
    useMyProjectIds(currentUserDbId);

  const {
    search,
    projectFilter,
    viewFilter,
    onlyMyProjects,
    plotFilter,
    assignedToFilter,
    deadlineWeek,
    procurementPackageFilter,
    workSectionFilter,
    locationFilter,
    taskTypeFilter,
    statusFilter,
    setSearch,
    setProjectFilter,
    setViewFilter,
    setPlotFilter,
    setAssignedToFilter,
    setDeadlineWeek,
    setProcurementPackageFilter,
    setWorkSectionFilter,
    setLocationFilter,
    setTaskTypeFilter,
    setStatusFilter,
    resetFilters,
  } = useTaskFilterStore();

  const committedSearch = useDebouncedValue(search, 300);

  const hasLoadedOnce = useRef(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskExtended | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Active status tab — the desktop's three Kanban columns, one snapped into
  // view at a time on mobile.
  const [activeStatus, setActiveStatus] = useState<StatusTabValue>("IN_PROGRESS");

  // Per-status totalCount, reported up by MobileTaskList. Used to render
  // counts on the inactive tabs as well as the active one.
  const [counts, setCounts] = useState<Record<StatusTabValue, number>>({
    PENDING: 0,
    IN_PROGRESS: 0,
    COMPLETED: 0,
  });

  const { refs: refetchRefs, refetchAll: refetchAllColumns, refetchColumn } = useColumnRefetch();

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [projects]);
  const plotNameById = useMemo(() => {
    const m = new Map<string, string>();
    plots.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [plots]);
  const locationNameById = useMemo(
    () =>
      new Map(
        locations.map((l) => [
          l.id,
          l.hierarchyPath.length > 0 ? l.hierarchyPath.join(" › ") : l.name,
        ]),
      ),
    [locations],
  );

  const {
    validProjectIdSet,
    validPlotIdSet,
    validPpKeySet,
    validSectionKeySet,
    ppOptions,
    ppNameMap,
    sectionOptions,
    sectionNameMap,
    validLocationIdSet,
    locationOptions,
  } = useCascadingFilterScopes({
    projects,
    plots,
    procurementPackages,
    sections,
    locations,
    myProjectIdSet,
    myProjectRolesLoading,
    seesAllProjects,
  });

  const serverFilterParams = useServerFilterParams({
    committedSearch,
    validProjectIdSet,
    validPlotIdSet,
    validPpKeySet,
    validSectionKeySet,
    validLocationIdSet,
    ppNameMap,
    sectionNameMap,
    myProjectRolesLoading,
    seesAllProjects,
    currentAzureUserId: user?.id,
    users,
  });

  // Week-bucket aggregation for the WeekSelector (deadline-week selection
  // stripped so chips reflect what's present absent that filter).
  const bucketFilterParams = useMemo(
    () => buildBucketFilterParams(serverFilterParams),
    [serverFilterParams],
  );
  const {
    buckets,
    totalCount: weekTotalCount,
    noDeadlineCount,
    loading: bucketsLoading,
    refetch: refetchBuckets,
  } = useTaskWeekBuckets(bucketFilterParams);

  const { updateTaskStatus, createTask } = useTaskMutations();

  const refetchAllColumnsAndBuckets = useCallback(() => {
    refetchAllColumns();
    refetchBuckets();
  }, [refetchAllColumns, refetchBuckets]);
  const toggleTaskCompleted = useTaskCompletionToggle(
    refetchAllColumnsAndBuckets,
  );
  const refetchColumnAndBuckets = useCallback(
    (status: TaskStatus) => {
      refetchColumn(status);
      refetchBuckets();
    },
    [refetchColumn, refetchBuckets],
  );

  useTaskSubscription(
    serverFilterParams.projectIds,
    refetchAllColumnsAndBuckets,
    refetchColumnAndBuckets,
  );

  // See desktop page: included in MobileTaskList's key so the virtualizer is
  // rebuilt from scratch when the result set can reorder substantially.
  const columnResetKey = useMemo(
    () => JSON.stringify(serverFilterParams),
    [serverFilterParams],
  );

  // Same effective-statuses pruning as desktop. When the global statusFilter
  // empties a column's status set, the list skips its query and shows empty.
  const effectiveColumns = useMemo(() => {
    if (statusFilter.length === 0) return KANBAN_COLUMNS;
    return KANBAN_COLUMNS.map((col) => {
      const native = col.statuses ?? [col.status];
      const intersected = native.filter((s) => statusFilter.includes(s));
      return { ...col, statuses: intersected };
    });
  }, [statusFilter]);

  useEffect(() => {
    if (!projectsLoading) {
      hasLoadedOnce.current = true;
    }
  }, [projectsLoading]);

  const initialLoading = !hasLoadedOnce.current && projectsLoading;

  const hasFilters =
    committedSearch ||
    search ||
    projectFilter.length > 0 ||
    viewFilter !== "Kaikkien" ||
    !onlyMyProjects ||
    plotFilter.length > 0 ||
    assignedToFilter.length > 0 ||
    procurementPackageFilter.length > 0 ||
    workSectionFilter.length > 0 ||
    locationFilter.length > 0 ||
    taskTypeFilter.length > 0 ||
    statusFilter.length > 0;

  const facetFilterCount =
    projectFilter.length +
    plotFilter.length +
    procurementPackageFilter.length +
    workSectionFilter.length +
    locationFilter.length +
    assignedToFilter.length +
    taskTypeFilter.length +
    (!onlyMyProjects ? 1 : 0);

  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const swipeRef = useRef<HTMLDivElement | null>(null);
  const activeStatusRef = useRef(activeStatus);
  const programmaticScroll = useRef(false);
  const programmaticTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollFrame = useRef<number | null>(null);

  // While a dialog is open the column swipe is frozen by dropping touch-action
  // to pan-y. Toggling overflow-x would clamp scrollLeft back to column one.
  const swipeLocked = dialogOpen || createDialogOpen || filterSheetOpen;

  useEffect(() => {
    activeStatusRef.current = activeStatus;
  }, [activeStatus]);

  const scrollToStatus = useCallback(
    (status: StatusTabValue, behavior: ScrollBehavior) => {
      const el = swipeRef.current;
      if (!el || el.clientWidth === 0) return;
      programmaticScroll.current = true;
      if (programmaticTimer.current) clearTimeout(programmaticTimer.current);
      programmaticTimer.current = setTimeout(() => {
        programmaticScroll.current = false;
      }, 500);
      el.scrollTo({ left: STATUS_ORDER.indexOf(status) * el.clientWidth, behavior });
    },
    [],
  );

  const goToStatus = useCallback(
    (status: StatusTabValue) => {
      setActiveStatus(status);
      scrollToStatus(status, "smooth");
    },
    [scrollToStatus],
  );

  const attachSwipeRef = useCallback((el: HTMLDivElement | null) => {
    swipeRef.current = el;
    if (el && el.clientWidth > 0) {
      el.scrollLeft = STATUS_ORDER.indexOf(activeStatusRef.current) * el.clientWidth;
    }
  }, []);

  const handleSwipeScroll = useCallback(() => {
    if (swipeLocked || programmaticScroll.current || scrollFrame.current !== null) return;
    scrollFrame.current = requestAnimationFrame(() => {
      scrollFrame.current = null;
      const el = swipeRef.current;
      if (!el || el.clientWidth === 0) return;
      const index = Math.min(
        STATUS_ORDER.length - 1,
        Math.max(0, Math.round(el.scrollLeft / el.clientWidth)),
      );
      const next = STATUS_ORDER[index];
      setActiveStatus((prev) => (prev === next ? prev : next));
    });
  }, [swipeLocked]);

  useEffect(() => {
    const onResize = () => scrollToStatus(activeStatusRef.current, "auto");
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (programmaticTimer.current) clearTimeout(programmaticTimer.current);
      if (scrollFrame.current !== null) cancelAnimationFrame(scrollFrame.current);
    };
  }, [scrollToStatus]);

  type Chip = { key: string; label: string; onRemove: () => void };
  const activeChips = useMemo<Chip[]>(() => {
    const chips: Chip[] = [];
    projectFilter.forEach((id) => {
      const name = projects.find((p) => p.id === id)?.name || id;
      chips.push({
        key: `project-${id}`,
        label: `Hanke: ${name}`,
        onRemove: () => setProjectFilter(projectFilter.filter((x) => x !== id)),
      });
    });
    plotFilter.forEach((id) => {
      const name = plots.find((p) => p.id === id)?.name || id;
      chips.push({
        key: `plot-${id}`,
        label: `Tontti: ${name}`,
        onRemove: () => setPlotFilter(plotFilter.filter((x) => x !== id)),
      });
    });
    procurementPackageFilter.forEach((key) => {
      const opt = ppOptions.find((o) => o.value === key);
      chips.push({
        key: `pp-${key}`,
        label: `Hankintapaketti: ${opt?.label || key}`,
        onRemove: () =>
          setProcurementPackageFilter(procurementPackageFilter.filter((x) => x !== key)),
      });
    });
    workSectionFilter.forEach((key) => {
      const opt = sectionOptions.find((o) => o.value === key);
      chips.push({
        key: `ws-${key}`,
        label: `Työvaihe: ${opt?.label || key}`,
        onRemove: () => setWorkSectionFilter(workSectionFilter.filter((x) => x !== key)),
      });
    });
    locationFilter.forEach((id) => {
      chips.push({
        key: `location-${id}`,
        label: `Sijainti: ${locationNameById.get(id) ?? id}`,
        onRemove: () => setLocationFilter(locationFilter.filter((x) => x !== id)),
      });
    });
    assignedToFilter.forEach((id) => {
      const u = users.find((usr) => usr.id === id);
      const name = u
        ? u.firstName && u.lastName
          ? `${u.firstName} ${u.lastName}`
          : u.email
        : id;
      chips.push({
        key: `user-${id}`,
        label: `Vastuu: ${name}`,
        onRemove: () => setAssignedToFilter(assignedToFilter.filter((x) => x !== id)),
      });
    });
    taskTypeFilter.forEach((tt) => {
      const label = TASK_TYPE_OPTIONS.find((o) => o.value === tt)?.label || tt;
      chips.push({
        key: `tt-${tt}`,
        label: `Tyyppi: ${label}`,
        onRemove: () => setTaskTypeFilter(taskTypeFilter.filter((x) => x !== tt)),
      });
    });
    statusFilter.forEach((st) => {
      const label = TASK_STATUS_LABELS_SINGULAR[st] || st;
      chips.push({
        key: `st-${st}`,
        label: `Tila: ${label}`,
        onRemove: () => setStatusFilter(statusFilter.filter((x) => x !== st)),
      });
    });
    return chips;
  }, [
    projectFilter,
    plotFilter,
    procurementPackageFilter,
    workSectionFilter,
    locationFilter,
    assignedToFilter,
    taskTypeFilter,
    statusFilter,
    projects,
    plots,
    ppOptions,
    sectionOptions,
    locationNameById,
    users,
    setProjectFilter,
    setPlotFilter,
    setProcurementPackageFilter,
    setWorkSectionFilter,
    setLocationFilter,
    setAssignedToFilter,
    setTaskTypeFilter,
    setStatusFilter,
  ]);

  const openTaskDialog = (task: TaskExtended) => {
    setSelectedTask(task);
    setDialogOpen(true);
  };

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      if (!open && selectedTask) {
        refetchColumn(selectedTask.status);
        refetchBuckets();
      }
    },
    [selectedTask, refetchColumn, refetchBuckets],
  );

  const handleUpdateStatus = async (taskId: string, status: TaskStatus) => {
    try {
      if (selectedTask?.id === taskId && selectedTask.version != null) {
        await updateTaskStatus(taskId, status, selectedTask.version);
      }
      setDialogOpen(false);
    } catch (error) {
      console.error("Failed to update task status:", error);
    }
  };

  const activeUsers = useMemo(() => users.filter((u) => u.isActive), [users]);

  const currentUserId = useMemo(() => {
    if (!user?.id) return "";
    const currentUser = users.find((u) => u.azureObjectId === user.id);
    return currentUser?.id || "";
  }, [user?.id, users]);

  // Stable per-tab count callbacks so MobileTaskList's effect dep is stable.
  const onCountPending = useCallback(
    (n: number) => setCounts((c) => (c.PENDING === n ? c : { ...c, PENDING: n })),
    [],
  );
  const onCountInProgress = useCallback(
    (n: number) => setCounts((c) => (c.IN_PROGRESS === n ? c : { ...c, IN_PROGRESS: n })),
    [],
  );
  const onCountCompleted = useCallback(
    (n: number) => setCounts((c) => (c.COMPLETED === n ? c : { ...c, COMPLETED: n })),
    [],
  );

  if (initialLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="shrink-0 border-b bg-background px-3 pt-3 pb-2 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-9 w-9 rounded-md ml-auto" />
            <Skeleton className="h-9 w-9 rounded-md" />
          </div>
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="flex-1 overflow-y-auto px-3 pt-3 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Build per-status column lookup so we can render the active list while
  // still keeping inactive lists mounted (so their counts stay live).
  const colByStatus: Record<StatusTabValue, (typeof effectiveColumns)[number]> = {
    PENDING: effectiveColumns[0],
    IN_PROGRESS: effectiveColumns[1],
    COMPLETED: effectiveColumns[2],
  };
  const refByStatus = {
    PENDING: refetchRefs.pending,
    IN_PROGRESS: refetchRefs.inProgress,
    COMPLETED: refetchRefs.completed,
  };
  const onCountByStatus = {
    PENDING: onCountPending,
    IN_PROGRESS: onCountInProgress,
    COMPLETED: onCountCompleted,
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sticky toolbar */}
      <div className="shrink-0 border-b bg-background">
        <div className="px-3 pt-3 pb-2 space-y-2">
          {/* Row 1: title + view tabs + create */}
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold truncate">Tehtävähallinta</h1>
            <Tabs
              value={viewFilter}
              onValueChange={(v) => setViewFilter(v as "Kaikkien" | "Minun")}
              className="ml-auto"
            >
              <TabsList className="grid grid-cols-2 h-9 w-[140px] border border-input bg-muted/60 shadow-sm">
                <TabsTrigger
                  value="Minun"
                  className="text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
                >
                  Minun
                </TabsTrigger>
                <TabsTrigger
                  value="Kaikkien"
                  className="text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
                >
                  Kaikkien
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              size="icon"
              onClick={() => setCreateDialogOpen(true)}
              title="Luo uusi tehtävä"
              className="h-9 w-9 shrink-0"
            >
              <AddIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Row 2: search + filter */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Hae..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-11"
              />
            </div>
            <div className="relative shrink-0">
              <Button
                variant="outline"
                onClick={() => setFilterSheetOpen(true)}
                title="Suodata"
                className="h-11 gap-2 px-4 text-sm font-medium"
              >
                <FiltersIcon className="h-5 w-5" />
                Suodata
              </Button>
              {facetFilterCount > 0 && (
                <Badge
                  variant="secondary"
                  className="pointer-events-none absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 text-[9px] tabular-nums"
                >
                  {facetFilterCount}
                </Badge>
              )}
            </div>
            <MemoListToggle className="h-11 w-11" />
          </div>

          {/* Row 2b: week selector (replaces the deadline preset chips) */}
          {/* Full-bleed: -mx-3 cancels the page gutter so chips scroll to the
              screen edges; the selector supplies its own px-3 inset. */}
          <div className="-mx-3">
            <WeekSelector
              value={deadlineWeek}
              onChange={setDeadlineWeek}
              buckets={buckets}
              totalCount={weekTotalCount}
              noDeadlineCount={noDeadlineCount}
              loading={bucketsLoading}
            />
          </div>

          <div className="max-h-[40dvh] overflow-y-auto empty:hidden">
            <MemoListStack />
          </div>

          {/* Row 3: status tabs with counts */}
          <Tabs
            value={activeStatus}
            onValueChange={(v) => goToStatus(v as StatusTabValue)}
          >
            <TabsList className="grid grid-cols-3 w-full h-9">
              <TabsTrigger value="PENDING" className="text-xs gap-1.5">
                <span className="h-2 w-2 rounded-full bg-slate-500" />
                <span className="truncate">Ei aloitettu</span>
                <span className="text-muted-foreground tabular-nums">
                  ({counts.PENDING})
                </span>
              </TabsTrigger>
              <TabsTrigger value="IN_PROGRESS" className="text-xs gap-1.5">
                <span className="h-2 w-2 rounded-full bg-yellow-500" />
                <span className="truncate">Kesken</span>
                <span className="text-muted-foreground tabular-nums">
                  ({counts.IN_PROGRESS})
                </span>
              </TabsTrigger>
              <TabsTrigger value="COMPLETED" className="text-xs gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-500" />
                <span className="truncate">Valmis</span>
                <span className="text-muted-foreground tabular-nums">
                  ({counts.COMPLETED})
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Row 5: active filter chips */}
          {activeChips.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto -mx-3 px-3 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {activeChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-accent/60 px-2.5 py-1 text-xs text-accent-foreground border border-border/40"
                >
                  <span className="truncate max-w-[160px]">{chip.label}</span>
                  <button
                    type="button"
                    onClick={chip.onRemove}
                    className="rounded-full p-0.5 hover:bg-background/80 transition-colors"
                    aria-label={`Poista suodatin: ${chip.label}`}
                  >
                    <CloseIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs shrink-0"
                onClick={() => resetFilters()}
              >
                Tyhjennä
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Task lists — all three sit side by side in a snap-scrolling row, so a
          horizontal swipe moves between columns. Keeping every list mounted
          lets the tab counts stay live and preserves pagination state. */}
      <div
        ref={attachSwipeRef}
        onScroll={handleSwipeScroll}
        style={{ touchAction: swipeLocked ? "pan-y" : "auto" }}
        className="flex-1 min-h-0 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {STATUS_ORDER.map((status) => (
          <div key={status} className="w-full shrink-0 snap-center flex flex-col min-h-0">
            <MobileTaskList
              key={`${status}-${columnResetKey}`}
              column={colByStatus[status]}
              filterParams={serverFilterParams}
              projectNameById={projectNameById}
              plotNameById={plotNameById}
              onTaskClick={openTaskDialog}
              onToggleCompleted={toggleTaskCompleted}
              refetchRef={refByStatus[status]}
              onCount={onCountByStatus[status]}
            />
          </div>
        ))}
      </div>

      {/* Filter sheet — full-screen on mobile */}
      <Dialog open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
        <DialogContent className="max-w-full w-screen h-dvh max-h-dvh rounded-none sm:rounded-none flex flex-col p-4 gap-0">
          <DialogHeader>
            <DialogTitle>Suodata tehtäviä</DialogTitle>
            <DialogDescription>
              Rajaa näkymää hankkeen, tontin, hankintapaketin tai muiden kenttien mukaan.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-3 -mx-4 px-4">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Hanke</Label>
                <MultiSelectDropdown
                  options={projects.map((p) => ({
                    value: p.id,
                    label: p.name,
                    disabled: !validProjectIdSet.has(p.id),
                  }))}
                  value={projectFilter}
                  onChange={setProjectFilter}
                  placeholder="Kaikki hankkeet"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Tontti</Label>
                <MultiSelectDropdown
                  options={plots.map((plot) => ({
                    value: plot.id,
                    label: plot.name,
                    disabled: !validPlotIdSet.has(plot.id),
                  }))}
                  value={plotFilter}
                  onChange={setPlotFilter}
                  placeholder="Kaikki tontit"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Hankintapaketti</Label>
                <MultiSelectDropdown
                  options={ppOptions.map((opt) => ({
                    ...opt,
                    disabled: !validPpKeySet.has(opt.value),
                  }))}
                  value={procurementPackageFilter}
                  onChange={setProcurementPackageFilter}
                  placeholder="Kaikki hankintapaketit"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Työvaihe</Label>
                <MultiSelectDropdown
                  options={sectionOptions.map((opt) => ({
                    ...opt,
                    disabled: !validSectionKeySet.has(opt.value),
                  }))}
                  value={workSectionFilter}
                  onChange={setWorkSectionFilter}
                  placeholder="Kaikki työvaiheet"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Sijainti</Label>
                <MultiSelectDropdown
                  options={locationOptions.map((opt) => ({
                    ...opt,
                    disabled: !validLocationIdSet.has(opt.value),
                  }))}
                  value={locationFilter}
                  onChange={setLocationFilter}
                  placeholder="Kaikki sijainnit"
                  className="w-full"
                />
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Vastuuhenkilö</Label>
                  <MultiSelectDropdown
                    options={activeUsers.map((u) => ({
                      value: u.id,
                      label:
                        u.firstName && u.lastName
                          ? `${u.firstName} ${u.lastName}`
                          : u.email,
                    }))}
                    value={assignedToFilter}
                    onChange={setAssignedToFilter}
                    placeholder="Kaikki henkilöt"
                    className="w-full"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Tehtävätyyppi</Label>
                  <MultiSelectDropdown
                    options={TASK_TYPE_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    value={taskTypeFilter}
                    onChange={setTaskTypeFilter}
                    placeholder="Kaikki tehtävätyypit"
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter align="between">
            <Button
              variant="ghost"
              onClick={() => resetFilters()}
              disabled={!hasFilters}
            >
              Tyhjennä kaikki
            </Button>
            <Button onClick={() => setFilterSheetOpen(false)}>Valmis</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Dialog */}
      <TaskDialog
        task={selectedTask}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        onUpdateStatus={handleUpdateStatus}
        currentUserId={currentUserId}
        onTaskUpdated={setSelectedTask}
        onSubtasksChanged={refetchAllColumns}
      />

      {/* Create Task Dialog */}
      <CreateTaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        projects={projects}
        plots={plots}
        procurementPackages={procurementPackages}
        users={users}
        currentUserId={currentUserId}
        createTask={createTask}
        onTaskCreated={(task) => {
          setSelectedTask(task);
          setDialogOpen(true);
        }}
        onTasksRefresh={refetchAllColumns}
      />
    </div>
  );
}
